import {
    ChangeDetectionStrategy,
    Component,
    computed,
    DestroyRef,
    effect,
    inject,
    OnDestroy,
    OnInit,
    signal,
} from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { TranslocoService } from '@jsverse/transloco';
import { firstValueFrom } from 'rxjs';

import { DepositBloc } from '@connect-services/client/blocs/deposit.bloc';
import { DepositFolderListBloc } from '@connect-services/client/blocs/deposit-folder-list.bloc';
import { ModalType } from '@connect-services/client/components/abstract-modal/abstract-modal.component';
import { ConfirmationModal } from '@connect-services/client/components/confirmation-modal/confirmation-modal.component';
import { EntityLinkDocumentListComponent } from '@connect-services/client/components/entity-link-document-list/entity-link-document-list.component';
import { ModalResultEnum } from '@connect-services/client/models/common.model';
import { NotificationEventEnum } from '@connect-services/client/models/notification.model';
import { RouteCompletePathEnum } from '@connect-services/client/models/routes.model';
import { DataViewType } from '@connect-services/client/pages/management/data/deposit/deposit.component';
import {
    DepositMetadataInformationComponent,
    MetadataInformation,
} from '@connect-services/client/pages/management/data/deposit/deposit-metadata-information/deposit-metadata-information.component';
import { DepositValidationComponent } from '@connect-services/client/pages/management/data/deposit/deposit-validation/deposit-validation.component';
import { CreateDepositLayerService } from '@connect-services/client/services/deposit/layer/create-deposit-layer.service';
import { DetailPanelService, PanelAction } from '@connect-services/client/services/detail-panel.service';
import { DocumentIdentifiers, DocumentService } from '@connect-services/client/services/document.service';
import { BehaviourButton, ManagementDataService } from '@connect-services/client/services/management-data.service';
import { NotificationService } from '@connect-services/client/services/notification.service';
import { ButtonType } from '@connect-services/src/lib/components/button/button.component';
import {
    Deposit,
    DepositFolder,
    DepositsService,
    DepositTable,
    IsDeletableDeposit,
    PermissionAliasEnum,
    Project,
    ProjectsService,
} from '@connect-services/src/sdk/api';
import ValidateDeposit from '@connect-services/src/sdk/async/ValidateDeposit';
import { RessourcesModule } from '@ressources/client/ressources.module';
import { SnackBarService } from '@shared/client/services/snack-bar.service';
import StatusEnum = Deposit.StatusEnum;

@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        DepositMetadataInformationComponent,
        DepositValidationComponent,
        EntityLinkDocumentListComponent,
        RessourcesModule,
    ],
    selector: 'app-deposit-detail',
    styleUrl: './deposit-detail.component.scss',
    templateUrl: './deposit-detail.component.html',
})
export class DepositDetailComponent implements OnInit, OnDestroy {
    public readonly deposit = signal<Deposit | undefined>(undefined);

    protected readonly ButtonType = ButtonType;
    protected readonly createDepositLayerService = inject(CreateDepositLayerService);
    protected readonly DataViewType = DataViewType;
    protected readonly deleteActionState = computed<BehaviourButton>((): BehaviourButton => {
        return this.managementDataService.setDeleteActionState({
            deposit: this.deposit(),
            isDeletable: {
                hasLostDocuments: this.deletionStatus()?.hasLostDocuments ?? false,
                isDepositUsedInLayer: this.deletionStatus()?.isDepositUsedInLayer ?? false,
            },
        });
    });
    protected readonly Deposit = Deposit;
    protected readonly documentData = computed<DocumentIdentifiers>(() => {
        return {
            depositId: this.deposit()?.id ?? '',
            lostVersion: this.deposit()?.version ?? 0,
        };
    });
    protected readonly documentService = inject(DocumentService);
    protected readonly managementDataService = inject(ManagementDataService);
    protected readonly metadataInformation = signal<MetadataInformation | null>(null);
    protected readonly PermissionAliasEnum = PermissionAliasEnum;
    protected readonly projects = signal<Project[]>([]);

    private readonly deletionStatus = signal<IsDeletableDeposit | null>(null);
    private depositBloc = inject(DepositBloc);
    private depositFolderListBloc = inject(DepositFolderListBloc);
    private depositsService = inject(DepositsService);
    private destroyRef = inject(DestroyRef);
    private readonly detailPanelService = inject(DetailPanelService);
    private readonly isDepositValidationInProgress = computed<boolean>(() => {
        const status: StatusEnum | undefined = this.deposit()?.status;

        return (
            status != null &&
            [Deposit.StatusEnum.EXTRACTING, Deposit.StatusEnum.VALIDATING, Deposit.StatusEnum.IMPORTING].includes(
                status,
            )
        );
    });
    private readonly matDialog = inject(MatDialog);
    private readonly notificationService = inject(NotificationService<ValidateDeposit>);
    private readonly projectsService = inject(ProjectsService);
    private readonly router = inject(Router);
    private readonly snackBarService = inject(SnackBarService);
    private readonly translocoService = inject(TranslocoService);

    public constructor() {
        this.destroyRef.onDestroy(() => {
            this.detailPanelService.clear();
        });

        effect(async () => {
            if (!this.depositBloc.value()) {
                return;
            }

            await this.initData();
        });

        effect(() => {
            if (this.managementDataService.currentView() !== DataViewType.REFERENCE_DATA) {
                return;
            }

            this.buildMetadataInformation();
        });

        effect(() => {
            if (!this.deposit()) {
                return;
            }

            this.managementDataService.abortingDepositIds();

            this.detailPanelService.actions.set(this.getPanelActions());
        });
    }

    public ngOnDestroy(): void {
        this.notificationService.off(
            NotificationEventEnum.DEPOSIT_VALIDATION,
            this.managementDataService.refreshDeposit,
        );
    }

    public ngOnInit(): void {
        this.notificationService.on(
            NotificationEventEnum.DEPOSIT_VALIDATION,
            this.managementDataService.refreshDeposit,
        );
    }

    private buildMetadataInformation(): void {
        const deposit = this.deposit();

        if (!deposit) {
            return;
        }

        const depositFolder: DepositFolder | undefined = this.depositFolderListBloc.value()?.items.find((item) => {
            return item.id === deposit.depositFolder;
        });

        const referenceData: MetadataInformation = {
            depositFolderMetadata: depositFolder?.metadata ?? null,
            depositMetadata: this.deposit()?.metadata ?? null,
            standardMetadata: depositFolder?.standard?.metadata ?? null,
        };

        this.metadataInformation.set(referenceData);
    }

    private getPanelActions(): PanelAction[] {
        const deposit = this.deposit();
        if (!deposit) {
            return [];
        }

        if (deposit.status === Deposit.StatusEnum.IMPORTING) {
            const isAbortInProgress = this.managementDataService.abortingDepositIds().has(deposit.id);

            return [
                new PanelAction({
                    icon: ['fas', 'xmark'],
                    isActive: !isAbortInProgress,
                    label: this.translocoService.translate('stopImport'),
                    loading: isAbortInProgress,
                    onClick: (): void => {
                        this.managementDataService.openAbortDepositImportConfirmation(deposit);
                    },
                    type: ButtonType.ALERT_SECONDARY,
                }),
            ];
        }

        const canEditDeposit =
            this.managementDataService.canManageDeposit() &&
            !this.deposit()?.standard &&
            !this.isDepositValidationInProgress();

        return [
            new PanelAction({
                defaultDisabledTooltipMessage: this.translocoService.translate('notPossibleDelete'),
                disabledReason: this.deleteActionState().disabledMessage,
                icon: ['fas', 'trash'],
                isActive: this.deleteActionState().isActive,
                label: this.translocoService.translate('delete'),
                onClick: this.handleOnDelete.bind(this),
                type: ButtonType.ALERT_SECONDARY,
            }),
            new PanelAction({
                defaultDisabledTooltipMessage: this.translocoService.translate('notPossibleEdit'),
                icon: ['fas', 'pen'],
                isActive: canEditDeposit,
                label: this.translocoService.translate('edit'),
                onClick: (): Promise<void> =>
                    this.managementDataService.openDepositEdition(this.depositBloc.value()!.id),
            }),
        ];
    }

    private async getProjects(): Promise<Project[]> {
        return (
            await firstValueFrom(
                this.projectsService.projectControllerGet({
                    deposit: this.deposit()!.id,
                }),
            )
        ).items;
    }

    private getTables(): Promise<DepositTable[]> {
        return firstValueFrom(
            this.depositsService.depositControllerGetTables({
                id: this.deposit()!.id,
            }),
        );
    }

    private handleOnDelete(): void {
        const dialogRef = this.matDialog.open(ConfirmationModal, {
            data: {
                cancelLabel: this.translocoService.translate('cancel'),
                confirmLabel: this.translocoService.translate('delete'),
                description: this.translocoService.translate('irreversibleAction'),
                title: this.translocoService.translate('confirmDeleteDeposit'),
                type: ModalType.DANGER,
            },
            width: '25rem',
        });

        dialogRef.afterClosed().subscribe(async (result: ModalResultEnum) => {
            if (result !== ModalResultEnum.CONFIRM) {
                return;
            }

            try {
                await firstValueFrom(
                    this.depositsService.depositControllerDelete({
                        id: this.depositBloc.value()!.id,
                    }),
                );

                this.snackBarService.openSuccessSnackBar('depositDeletedSuccess');

                await this.managementDataService.refreshDepositFolderList();

                await this.router.navigate([...RouteCompletePathEnum.ORGANIZATION_DATA]);
            } catch {
                this.snackBarService.openErrorSnackBar('depositDeletedError');
            }
        });
    }

    private async initData(): Promise<void> {
        await this.initDepositData();
        this.initPanelData();
    }

    private async initDepositData(): Promise<void> {
        this.deposit.set(this.depositBloc.value()!);
        this.projects.set(await this.getProjects());

        if (this.isDepositValidationInProgress() || !this.deposit()?.depositValidation?.isValid) {
            return;
        }

        this.createDepositLayerService.tables.set(await this.getTables());
    }

    private initPanelData(): void {
        this.detailPanelService.title.set(this.deposit()!.name);
        this.detailPanelService.navigateTo.set(RouteCompletePathEnum.ORGANIZATION_DATA);

        const deletionStatus = this.depositBloc.value()!.isDeletableDeposit;
        if (deletionStatus) {
            this.deletionStatus.set(deletionStatus);
        }

        this.detailPanelService.actions.set(this.getPanelActions());

        const currentView = this.managementDataService.currentView();
        this.detailPanelService.navigateTo.set(
            currentView === DataViewType.ORGANIZATION_DATA
                ? RouteCompletePathEnum.ORGANIZATION_DATA
                : RouteCompletePathEnum.REFERENCE_DATA,
        );
    }
}
